import React, { useState, useEffect } from 'react';
import { Table, Input, Card } from 'reactstrap';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  useDisclosure,
  Flex,
  Text,
  Select,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from '@chakra-ui/react';
import httpInjectorService from 'services/http-injector.service';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BulletList } from 'react-content-loader';
import { Empty } from 'antd';
import { demoDepartments } from 'components/demodata/Dummydata';

const DepartmentTable = ({ activeTab }) => {
  const [departments, setDepartments] = useState([]);
  const [departmentHeadUsers, setDepartmentHeadUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const [isEditing, setIsEditing] = useState(false);
  const [currentDepartmentIndex, setCurrentDepartmentIndex] = useState(null);
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [deleteDeptId, setDeleteDeptId] = useState(null);

  const [formData, setFormData] = useState({
    department: '',
    departmentHead: '',
    subDepartments: [{ subDepartment: '', subDepartmentId: '' }],
  });

  const [errors, setErrors] = useState({
    department: '',
    subDepartments: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Validate to disallow empty or spaces-only input
    if (!value.trim()) {
      setErrors({
        ...errors,
        [name]: 'This field cannot be empty or contain only spaces.',
      });
    } else {
      setErrors({ ...errors, [name]: '' }); // Clear error on valid input
    }
    setFormData({
      ...formData,
      [name]: value, // Don't trim spaces here
    });
  };

  const handleSubDepartmentChange = (index, e) => {
    const { name, value } = e.target;
    const newSubDepartments = [...formData.subDepartments];
    newSubDepartments[index] = {
      ...newSubDepartments[index],
      [name]: value, // Don't trim spaces here
    };
    setFormData({ ...formData, subDepartments: newSubDepartments });

    setErrors((prevErrors) => {
      const newSubErrors = [...prevErrors.subDepartments];
      if (!value.trim()) {
        newSubErrors[index] =
          'Sub-department cannot be empty or contain only spaces.';
      } else {
        newSubErrors[index] = '';
      }
      return { ...prevErrors, subDepartments: newSubErrors };
    });
  };

  // Note that you may need to update your addSubDepartment function if you have validation there as well.

  const addSubDepartment = () => {
    const { subDepartments } = formData;
    if (subDepartments[0].subDepartment) {
      setFormData({
        ...formData,
        subDepartments: [
          ...subDepartments,
          { subDepartment: '', subDepartmentId: '' },
        ],
      });
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        subDepartments: [
          'Please fill the first sub-department before adding another.',
        ],
      }));
    }
  };

  const removeSubDepartment = async (index) => {
    const newSubDepartments = formData.subDepartments.filter(
      (_, i) => i !== index,
    );

    // Only call deleteSubDepartment if it's an existing sub-department
    const subDepartmentId = formData.subDepartments[index].subDepartmentId; // Get the sub-department ID
    if (subDepartmentId) {
      const departmentId = departments[currentDepartmentIndex].id; // Get the current department ID
      await deleteSubDepartment(departmentId, subDepartmentId); // Pass both IDs
    await getData(); //refresh
    onEditClose(); // disable the edit modal after removeing the subdepartment
  }

    setFormData({ ...formData, subDepartments: newSubDepartments });
  };

  const validateSubDepartments = () => {
    const { subDepartments } = formData;
    const newSubErrors = [];
    let isValid = true;

    subDepartments.forEach((subDept, index) => {
      if (!subDept.subDepartment.trim()) {
        isValid = false;
        newSubErrors[index] =
          'Sub-department cannot be empty or contain only spaces.';
      } else {
        newSubErrors[index] = '';
      }
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      subDepartments: newSubErrors,
    }));

    return isValid;
  };

  const validateDepartmentName = (name) => {
    return name.trim() !== ''; // Check if name is not empty after trimming
  };

  const onSave = async () => {
    const { department, subDepartments } = formData;

    let hasError = false;
    if (!department.trim()) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        department:
          'Department name is required or cannot contain only spaces.',
      }));
      hasError = true;
    } else if (!validateDepartmentName(department)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        department: 'Department name must contain valid characters.',
      }));
      hasError = true;
    }

    if (!validateSubDepartments()) {
      hasError = true;
    }

    if (hasError) return;

    const dataToSend = {
      ...formData,
      subDepartments: subDepartments.map((subDept) => ({
        subDepartment: subDept.subDepartment,
        subDepartmentId: subDept.subDepartmentId,
      })),
    };

    if (isEditing) {
      await handleUpdateDepartment(dataToSend);
    } else {
      await handleAddDepartment(dataToSend);
    }
    onAddClose();
    onEditClose();
  };

  const handleAddDepartment = async (dataToSend) => {
    try {
      const payload = {
        ...dataToSend,
        departmentHead: dataToSend.departmentHead ? parseInt(dataToSend.departmentHead) : null,  // ✅ cast to int
      };
      const response = await httpInjectorService.postdepartment(payload);
      if (response.status === 'success') {
        await getData();  // ✅ refresh from API to get department_head_name resolved
        toast.success(response.message, { position: 'top-right', autoClose: 3000 });
      } else {
        toast.error(response.message, { position: 'top-right', autoClose: 3000 });
      }
    } catch (error) {
      toast.error(error, { position: 'top-right', autoClose: 2000 });
    }
  };

  const handleUpdateDepartment = async (dataToSend) => {
    try {
      const updatedDepartment = {
        ...departments[currentDepartmentIndex],
        ...dataToSend,
        departmentHead: dataToSend.departmentHead ? parseInt(dataToSend.departmentHead) : null,  // ✅ cast to int
      };
      const response = await httpInjectorService.updatedepartment(updatedDepartment);
      if (response.status === 'success') {
        await getData();  // ✅ refresh data from API instead of manually updating state
        toast.success('Department updated successfully');
      }
    } catch (error) {
      toast.error('Error updating department');
    }
  };

  const handleEditDepartment = (index) => {
    setCurrentDepartmentIndex(index);
    setIsEditing(true);
    setFormData({
      department: departments[index].department,
      departmentHead: departments[index].departmentHead,
      subDepartments: departments[index].subDepartments.map((subDept) => ({
        subDepartment: subDept.subDepartment,
        subDepartmentId: subDept.subDepartmentId,
      })),
    });
    onEditOpen();
  };

  const deleteDepartment = async () => {
    if (deleteDeptId) {
      try {
        const response = await httpInjectorService.deletedepartment({
          departmentId: deleteDeptId,
        });
        if (response.status === 'success') {
          toast.success('Department deleted successfully');
          getData();
          setDeleteDeptId(null);
          onDeleteClose();
        } else {
          toast.error(response.message, {
            position: 'top-right',
            autoClose: 3000,
          });
          setDeleteDeptId(null);
          onDeleteClose();
        }
      } catch (error) {
        toast.error(error, {
          position: 'top-right',
          autoClose: 2000,
        });
        setDeleteDeptId(null);
        onDeleteClose();
      }
    }
  };

  const deleteSubDepartment = async (deptId, subdeptId) => {
    try {
      const response = await httpInjectorService.deletedepartment({
        departmentId: deptId,
        subDepartmentId: subdeptId,
      });
      if (response.status === 'success') {
        toast.success('SubDepartment deleted');
      } else {
        // toast.error('Failed to delete subdepartment');
      }
    } catch (err) {
      toast.error(err, {
        position: 'top-right',
        autoClose: 2000,
      });
    }
  };

  // const getData = async () => {
  //   setLoading(true);
  //   try {
  //     const response = await httpInjectorService.getdepartment();
  //     if (response.data && response.data.length > 0) {
  //       const transformedData = response.data.map((dept) => ({
  //         id: dept.department_id,
  //         department: dept.department_name || 'N/A',
  //         departmentHead: dept.department_head || 'N/A',
  //         subDepartments: dept.subdepartments.map((subDept) => ({
  //           subDepartment: subDept.subdepartment,
  //           subDepartmentId: subDept.subdepartment_id,
  //           count: subDept.count,
  //         })),
  //       }));
  //       setDepartments(transformedData);
  //     } else {
  //       setDepartments([]);
  //     }
  //   } catch (error) {
  //     setDepartments([]);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const getData = async () => {
    setLoading(true);
    try {
      const isDemoMode = localStorage.getItem("demoMode") === "true";
            
   if (isDemoMode) {
  const transformedData = demoDepartments.map((dept) => ({
    id: dept.department_id,
    department: dept.department_name || "N/A",
    departmentHead: dept.department_head_id
      ? Number(dept.department_head_id)
      : "",
    departmentHeadName: dept.department_head_name || "N/A",
    subDepartments: (dept.subdepartments || []).map((subDept) => ({
      subDepartment: subDept.subdepartment,
      subDepartmentId: subDept.subdepartment_id,
      count: subDept.count,
    })),
  }));

  setDepartments(transformedData);
  setLoading(false);
  return;
}
      const response = await httpInjectorService.getdepartment();
      if (response.data && response.data.length > 0) {
        const transformedData = response.data.map((dept) => ({
          id: dept.department_id,
          department: dept.department_name || 'N/A',
          departmentHead: dept.department_head_id ? Number(dept.department_head_id) : '',
          departmentHeadName: dept.department_head_name || 'N/A',                       
          subDepartments: (dept.subdepartments || []).map((subDept) => ({
            subDepartment: subDept.subdepartment,
            subDepartmentId: subDept.subdepartment_id,
            count: subDept.count,
          })),
        }));
        setDepartments(transformedData);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === '4') {
      getData();
    }
  }, [activeTab]);

  const openAddModal = () => {
    setIsEditing(false);
    setFormData({
      department: '',
      departmentHead: '',
      subDepartments: [{ subDepartment: '', subDepartmentId: '' }],
    });
    setErrors({ department: '', subDepartments: [] }); // Reset errors
    onAddOpen();
  };

  const deleteDepartmentModal = (id) => {
    setDeleteDeptId(id);
    onDeleteOpen();
  };

  // fetch organizationUsers api call from db
  const fetchDepartmentHeadUsersLists = async () => {
    try {
      const response = await httpInjectorService.listorganizationusers();
      if (response.status === 'success') {
        setDepartmentHeadUsers(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    fetchDepartmentHeadUsersLists();
  }, []);

  return (
    <>
      <Card
        className="shadow bg-white mb-4"
        style={{
          marginTop: '20px',
          maxWidth: '960px',
          marginLeft: 'auto',
          marginRight: 'auto',
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.12)',
          // padding: '10px'
        }}
      >
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Departments</h5>
          <Tooltip label="Add Department" aria-label="Add Department">
            <Button colorScheme="purple" onClick={openAddModal}>
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          </Tooltip>
        </div>
        {loading ? (
          <div className="text-center">
            <BulletList />
          </div>
        ) : departments.length > 0 ? (
          <Box
            overflowX={{ base: 'auto', md: 'visible' }}
            overflowY="hidden"
            width="100%"
            maxHeight="100%" // add this — adjust height as needed
            style={{
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
            }}
            sx={{
              '&::-webkit-scrollbar': { height: '8px', width: '8px' }, // add width for vertical scrollbar,
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },
            }}
          >
            <Table bordered style={{ minWidth: '600px', width: '100%' }}>
              <thead
                style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: 'black',
                  position: 'sticky',
                  verticalAlign: 'middle',
                  // top: 0,
                  zIndex: 1,
                }}
              >
                <tr>
                  <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                    Serial No.
                  </th>
                  <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                    Department
                  </th>
                  <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                    Department Head
                  </th>
                  <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                    Sub Departments
                  </th>
                  <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                    Employees
                  </th>
                  <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                {departments.map((department, index) => (
                  <tr key={department.id}>
                    <td>{index + 1}</td>
                    <td>{department.department}</td>
                    <td>{department.departmentHeadName || 'N/A'}</td>
                    <td>
                      {department.subDepartments.map((subDept, subIndex) => (
                        <div key={subIndex}>{subDept.subDepartment}</div>
                      ))}
                    </td>
                    <td>
                      {department.subDepartments.map((subDept, idx) => (
                        <div key={idx}>{subDept.count}</div>
                      ))}
                    </td>
                    <td>
                      <Flex justify="center" align="center" gap={2}>
                        <Tooltip
                          label="Edit Department"
                          aria-label="Edit Department"
                        >
                          <Button
                            colorScheme="blue"
                            onClick={() => handleEditDepartment(index)}
                            size="sm"
                            // variant="outline"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        </Tooltip>
                        <Tooltip
                          label="Delete Department"
                          aria-label="Delete Department"
                        >
                          <Button
                            colorScheme="red"
                            onClick={() => deleteDepartmentModal(department.id)}
                            size="sm"
                            // variant="outline"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </Tooltip>
                      </Flex>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        ) : (
          <Card mt={2}>
            <Flex justify="center" align="center" height="200px">
              <Empty description="No data found" style={{ fontSize: '24px' }} />
            </Flex>
          </Card>
        )}
      </Card>

      {/* Modal for adding departments */}
      <Modal isOpen={isAddOpen} onClose={onAddClose} size="3xl">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
        <ModalContent
          bg="white"
          borderRadius="2xl"
          boxShadow="0 25px 50px rgba(0,0,0,0.15)"
          overflow="hidden"
        >
          <ModalHeader
            bgGradient="linear(to-r, purple.500, purple.700)"
            color="white"
            fontSize="lg"
            fontWeight="bold"
            py={4}
            px={6}
          >
            ➕ Add Department
          </ModalHeader>
          <ModalCloseButton color="white" mt={1} />
          <ModalBody pb={6} pt={6} px={8} bg="gray.50">
            <Flex direction="column" gap={4}>
              <FormControl>
                <FormLabel
                  color="gray.600"
                  fontSize="sm"
                  fontWeight="semibold"
                  mb={1}
                >
                  Department Name
                </FormLabel>
                <Input
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  placeholder="e.g. Engineering"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="lg"
                  _focus={{
                    borderColor: 'purple.400',
                    boxShadow: '0 0 0 2px rgba(159,122,234,0.3)',
                  }}
                  _placeholder={{ color: 'gray.400' }}
                />
                {errors.department && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {errors.department}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel
                  color="gray.600"
                  fontSize="sm"
                  fontWeight="semibold"
                  mb={1}
                >
                  Department Head
                </FormLabel>
                <Select
                  name="departmentHead"
                  value={formData.departmentHead}
                  onChange={handleInputChange}
                  placeholder="Select Department Head"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="lg"
                  _focus={{
                    borderColor: 'purple.400',
                    boxShadow: '0 0 0 2px rgba(159,122,234,0.3)',
                  }}
                >
                  {departmentHeadUsers
                    .filter((user) => user.IsActive === 1)
                    .map((user) => (
                      // ✅ Fix - sending user_id
                      <option key={user.user_id} value={user.user_id}>
                        {user.username} — {user.Designation} ({user.Department})
                      </option>
                    ))}
                </Select>
              </FormControl>

              <Box
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                p={4}
              >
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="gray.600"
                  mb={3}
                >
                  Sub Departments
                </Text>
                {formData.subDepartments.map((subDept, subdepartment_id) => (
                  <Flex key={subdepartment_id} mb={3} align="flex-end" gap={2}>
                    <FormControl>
                      <Input
                        name="subDepartment"
                        value={subDept.subDepartment}
                        onChange={(e) =>
                          handleSubDepartmentChange(subdepartment_id, e)
                        }
                        placeholder={`Sub Department ${subdepartment_id + 1}`}
                        bg="gray.50"
                        border="1px solid"
                        borderColor="gray.300"
                        borderRadius="lg"
                        _focus={{
                          borderColor: 'purple.400',
                          boxShadow: '0 0 0 2px rgba(159,122,234,0.3)',
                        }}
                        _placeholder={{ color: 'gray.400' }}
                      />
                      {errors.subDepartments[subdepartment_id] && (
                        <Text color="red.500" fontSize="xs" mt={1}>
                          {errors.subDepartments[subdepartment_id]}
                        </Text>
                      )}
                    </FormControl>
                    <Button
                      colorScheme="red"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSubDepartment(subdepartment_id)}
                      borderRadius="lg"
                      _hover={{ bg: 'red.50' }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </Flex>
                ))}
                <Button
                  colorScheme="purple"
                  variant="outline"
                  size="sm"
                  onClick={addSubDepartment}
                  borderRadius="lg"
                  leftIcon={<FontAwesomeIcon icon={faPlus} />}
                  mt={1}
                >
                  Add Sub Department
                </Button>
              </Box>
            </Flex>
          </ModalBody>

          <ModalFooter
            bg="gray.50"
            borderTop="1px solid"
            borderColor="gray.200"
            gap={2}
          >
            <Button
              colorScheme="purple"
              onClick={onSave}
              borderRadius="lg"
              px={6}
              _hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
              transition="all 0.2s"
            >
              Save
            </Button>
            <Button
              variant="outline"
              onClick={onAddClose}
              borderRadius="lg"
              px={6}
              _hover={{ bg: 'gray.100' }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal for editing/updating department */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="3xl">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
        <ModalContent
          bg="white"
          borderRadius="2xl"
          boxShadow="0 25px 50px rgba(0,0,0,0.15)"
          overflow="hidden"
        >
          <ModalHeader
            bgGradient="linear(to-r, blue.500, blue.700)"
            color="white"
            fontSize="lg"
            fontWeight="bold"
            py={4}
            px={6}
          >
            ✏️ Edit Department
          </ModalHeader>
          <ModalCloseButton color="white" mt={1} />
          <ModalBody pb={6} pt={6} px={8} bg="gray.50">
            <Flex direction="column" gap={4}>
              <FormControl>
                <FormLabel
                  color="gray.600"
                  fontSize="sm"
                  fontWeight="semibold"
                  mb={1}
                >
                  Department Name
                </FormLabel>
                <Input
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  placeholder="e.g. Engineering"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="lg"
                  _focus={{
                    borderColor: 'blue.400',
                    boxShadow: '0 0 0 2px rgba(99,179,237,0.3)',
                  }}
                  _placeholder={{ color: 'gray.400' }}
                />
                {errors.department && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {errors.department}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel
                  color="gray.600"
                  fontSize="sm"
                  fontWeight="semibold"
                  mb={1}
                >
                  Department Head
                </FormLabel>
                <Select
                  name="departmentHead"
                  value={formData.departmentHead}
                  onChange={handleInputChange}
                  placeholder="Select Department Head"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="lg"
                  _focus={{
                    borderColor: 'blue.400',
                    boxShadow: '0 0 0 2px rgba(99,179,237,0.3)',
                  }}
                >
                  {departmentHeadUsers
                    .filter((user) => user.IsActive === 1)
                    .map((user) => (
                      // ✅ Fix - sending user_id
                      <option key={user.user_id} value={user.user_id}>
                        {user.username} — {user.Designation} ({user.Department})
                      </option>
                    ))}
                </Select>
              </FormControl>

              <Box
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                p={4}
              >
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="gray.600"
                  mb={3}
                >
                  Sub Departments
                </Text>
                {formData.subDepartments.map((subDept, index) => (
                  <Flex key={index} mb={3} align="flex-end" gap={2}>
                    <FormControl>
                      <Input
                        name="subDepartment"
                        value={subDept.subDepartment}
                        onChange={(e) => handleSubDepartmentChange(index, e)}
                        placeholder={`Sub Department ${index + 1}`}
                        bg="gray.50"
                        border="1px solid"
                        borderColor="gray.300"
                        borderRadius="lg"
                        _focus={{
                          borderColor: 'blue.400',
                          boxShadow: '0 0 0 2px rgba(99,179,237,0.3)',
                        }}
                        _placeholder={{ color: 'gray.400' }}
                      />
                      {errors.subDepartments[index] && (
                        <Text color="red.500" fontSize="xs" mt={1}>
                          {errors.subDepartments[index]}
                        </Text>
                      )}
                    </FormControl>
                    <Button
                      colorScheme="red"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSubDepartment(index)}
                      borderRadius="lg"
                      _hover={{ bg: 'red.50' }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </Flex>
                ))}
                <Button
                  colorScheme="blue"
                  variant="outline"
                  size="sm"
                  onClick={addSubDepartment}
                  borderRadius="lg"
                  leftIcon={<FontAwesomeIcon icon={faPlus} />}
                  mt={1}
                >
                  Add Sub Department
                </Button>
              </Box>
            </Flex>
          </ModalBody>

          <ModalFooter
            bg="gray.50"
            borderTop="1px solid"
            borderColor="gray.200"
            gap={2}
          >
            <Button
              colorScheme="blue"
              onClick={onSave}
              borderRadius="lg"
              px={6}
              _hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
              transition="all 0.2s"
            >
              Save
            </Button>
            <Button
              variant="outline"
              onClick={onEditClose}
              borderRadius="lg"
              px={6}
              _hover={{ bg: 'gray.100' }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this department?
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDeleteClose} colorScheme="purple" mr={3}>
              No
            </Button>
            <Button colorScheme="red" onClick={deleteDepartment}>
              Yes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default DepartmentTable;
